import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import {
  ExerciseCreateDialogComponent,
  ExerciseCreateConfig,
  exerciseWorkspaceParams,
} from '../../../components/dialogs/exercise-create-dialog/exercise-create-dialog.component';
import { TestExerciseDispatchDialogComponent } from '../../../components/dialogs/test-exercise-dispatch-dialog/test-exercise-dispatch-dialog.component';
import { ExerciseImportDialogComponent } from '../../../components/dialogs/exercise-import-dialog/exercise-import-dialog.component';
import { DozentService, Exercise } from '../../../services/dozent.service';
import { TutorService, Tutor } from '../../../services/tutor.service';
import { BucketService, IBucket } from '../../../services/bucket.service';
import { AgentRefreshService } from '../../../agent/agent-refresh.service';

@Component({
  selector: 'app-dozent-exercises',
  templateUrl: './exercises.component.html',
  styleUrl: './exercises.component.scss',
})
export class DozentExercisesComponent implements OnInit {
  exercises: Exercise[] = [];
  isLoading = true;
  /** Ein fehlgeschlagener Ladevorgang sah bisher exakt wie "noch keine
   * Uebungen" aus: der Fehler wurde verschluckt und die Liste blieb leer.
   * Bei CORS-blockierten Antworten (status 0) toastet auch der globale
   * ErrorInterceptor nicht, der erst ab status >= 400 greift. */
  loadFailed = false;
  searchText = '';
  cardSize: 'small' | 'large' =
    (localStorage.getItem('al-exerciseCardSize') as 'small' | 'large') || 'large';
  exercisePage = 0;
  exercisePageSize = 25;
  exercisePageSizeOptions = [25, 50, 100];

  exerciseTypes = [
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'open-ended', label: 'Open Ended' },
    { value: 'fill-in-blank', label: 'Fill in the Blank' },
    { value: 'true-false', label: 'True / False' },
    { value: 'exam', label: 'Exam' },
  ];

  tutors: Tutor[] = [];
  allBuckets: IBucket[] = [];

  constructor(
    private dozentService: DozentService,
    private tutorService: TutorService,
    private bucketService: BucketService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private router: Router,
    private t: TranslateService
  ) {}

  private readonly agentRefresh = inject(AgentRefreshService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.loadExercises();
    // e.g. an exercise published from the Creator (creator.item.tutor.*)
    this.agentRefresh
      .on('exercises')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadExercises());
    this.bucketService.listBuckets();
    this.bucketService.buckets.subscribe((b) => (this.allBuckets = b));
    this.tutorService.tutors$.subscribe((t) => (this.tutors = t));
    if (this.tutorService.tutors$.value.length === 0) {
      this.tutorService.listTutors().subscribe();
    }
  }

  loadExercises() {
    this.isLoading = true;
    this.loadFailed = false;
    this.dozentService.listExercises().subscribe({
      next: (exercises) => {
        this.exercises = exercises;
        this.clampExercisePage();
        this.isLoading = false;
      },
      error: () => {
        this.loadFailed = true;
        this.isLoading = false;
        this.toastrService.danger(
          this.t.instant('pages.dozent.exercises.loadErrorHint'),
          this.t.instant('pages.dozent.exercises.loadError')
        );
      },
    });
  }

  get filteredExercises(): Exercise[] {
    if (!this.searchText) return this.exercises;
    const s = this.searchText.toLowerCase();
    return this.exercises.filter(
      (e) =>
        e.title.toLowerCase().includes(s) ||
        e.description?.toLowerCase().includes(s) ||
        e.exercise_type.toLowerCase().includes(s)
    );
  }

  get pagedExercises(): Exercise[] {
    const rows = this.filteredExercises;
    const maxPage = Math.max(Math.ceil(rows.length / this.exercisePageSize) - 1, 0);
    const page = Math.min(this.exercisePage, maxPage);
    const start = page * this.exercisePageSize;
    return rows.slice(start, start + this.exercisePageSize);
  }

  onSearchChange(): void {
    this.exercisePage = 0;
  }

  onCardSizeSelect(event: any) {
    const size = Array.isArray(event) ? event[0] : event;
    if (size !== 'small' && size !== 'large') return;
    this.cardSize = size;
    localStorage.setItem('al-exerciseCardSize', size);
  }

  onExercisePageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.filteredExercises.length / this.exercisePageSize) - 1, 0);
    this.exercisePage = Math.max(0, Math.min(page, maxPage));
  }

  onExercisePageSizeChange(size: number): void {
    this.exercisePageSize = Math.min(Math.max(size, 1), 100);
    this.exercisePage = 0;
  }

  openCreateDialog(mode: 'scratch' | 'kb') {
    this.dialogService.open(ExerciseCreateDialogComponent, {
      context: {
        mode,
        tutors: this.tutors,
        buckets: this.allBuckets,
        onConfirm: (config: ExerciseCreateConfig) =>
          this.handleCreateConfirm(config),
      },
    });
  }

  openTestExerciseDialog() {
    this.dialogService.open(TestExerciseDispatchDialogComponent);
  }

  openImportDialog() {
    this.dialogService
      .open(ExerciseImportDialogComponent, {
        context: {
          onImported: () => this.loadExercises(),
        },
      })
      .onClose.subscribe(
        (result?: { openExerciseId?: string; pendingBatchId?: string }) => {
          if (result?.openExerciseId) {
            this.router.navigate(['/dozent/exercises', result.openExerciseId]);
            return;
          }
          if (result?.pendingBatchId) {
            this.watchImportBatch(result.pendingBatchId);
          }
          this.loadExercises();
        }
      );
  }

  /** Import runs server-side; after the dialog is closed keep the list in
   * sync until the batch settles. */
  private watchImportBatch(batchId: string) {
    const timer = setInterval(() => {
      this.dozentService.getExerciseParseBatch(batchId).subscribe({
        next: (status) => {
          const settled = status.jobs.every(
            (j) => j.status === 'done' || j.status === 'error'
          );
          if (!settled) return;
          clearInterval(timer);
          this.loadExercises();
          const doneJobs = status.jobs.filter((j) => j.status === 'done');
          // A single CSV job can create N exercises — count exercises, not jobs.
          const created = doneJobs.reduce(
            (sum, j) => sum + ((j.result as any)?.count ?? 1),
            0
          );
          const failed = status.jobs.length - doneJobs.length;
          this.toastrService.success(
            this.t.instant('pages.dozent.exerciseImport.summary', {
              success: created,
              failed,
            }),
            ''
          );
        },
        error: () => clearInterval(timer),
      });
    }, 5000);
  }

  private handleCreateConfirm(config: ExerciseCreateConfig) {
    if (config.mode === 'scratch') {
      // Create exercise directly via API
      const payload: Partial<Exercise> = {
        title: config.title,
        description: config.description || undefined,
        exercise_type: config.exerciseType,
        max_score: config.maxScore,
        content: {
          questions: [],
          exercise_types: config.exerciseTypes,
          difficulty_level: config.difficultyLevel,
          learning_goals: config.learningGoals,
          scope: config.scope,
          language: 'de',
        },
      };
      this.dozentService.createExercise(payload).subscribe({
        next: (exercise: Exercise) => {
          this.toastrService.success(
            this.t.instant('pages.dozent.exercises.created'),
            ''
          );
          this.loadExercises();
          // Navigate to editor for the new exercise
          if (exercise?.id) {
            this.router.navigate(['/dozent/exercises', exercise.id]);
          }
        },
        error: () => {
          this.toastrService.danger(this.t.instant('toasts.error'), '');
        },
      });
    } else {
      // Navigate to creator with exercise config
      this.router.navigate(['/creator/workspace'], { queryParams: exerciseWorkspaceParams(config) });
    }
  }

  onDeleteExercise(exercise: Exercise) {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        onConfirm: () => {
          this.dozentService.deleteExercise(exercise.id).subscribe({
            next: () => {
              this.toastrService.success(
                this.t.instant('pages.dozent.exercises.deleted'),
                ''
              );
              this.loadExercises();
              ref.close();
            },
          });
        },
      },
    });
  }

  onTogglePublish(exercise: Exercise) {
    this.dozentService
      .updateExercise(exercise.id, { is_published: !exercise.is_published })
      .subscribe({
        next: () => this.loadExercises(),
      });
  }

  /** Freigabe für den KI-Übungsmodus. Unabhängig vom Publish-Status
   *  umschaltbar — ausgespielt wird nur published UND freigegeben. */
  onToggleChatPractice(exercise: Exercise) {
    this.dozentService
      .updateExercise(exercise.id, { chat_practice_enabled: !exercise.chat_practice_enabled })
      .subscribe({
        next: () => this.loadExercises(),
      });
  }

  onEditExercise(exercise: Exercise) {
    this.markImportSeen(exercise);
    this.router.navigate(['/dozent/exercises', exercise.id]);
  }

  /** Pencil: exercises from the Creator library are edited in the Creator (Q95/P4). */
  onPencil(exercise: Exercise) {
    if (exercise.creator_item_id) {
      this.router.navigate(['/creator/workspace'], { queryParams: { item: exercise.creator_item_id } });
    } else {
      this.onEditExercise(exercise);
    }
  }

  /** "Neu"-badge for imported drafts until first opened. Seen-state lives in
   * localStorage — the exercises table has no per-user seen flag. */
  private seenImports: Set<string> = new Set(
    JSON.parse(localStorage.getItem('al-seenImportedExercises') || '[]')
  );

  isNewImport(exercise: Exercise): boolean {
    return (
      !!exercise.content?.imported &&
      !exercise.is_published &&
      !this.seenImports.has(exercise.id)
    );
  }

  private markImportSeen(exercise: Exercise) {
    if (!exercise.content?.imported || this.seenImports.has(exercise.id)) return;
    this.seenImports.add(exercise.id);
    localStorage.setItem(
      'al-seenImportedExercises',
      JSON.stringify([...this.seenImports].slice(-500))
    );
  }

  getExerciseTypeLabel(type: string): string {
    return this.exerciseTypes.find((t) => t.value === type)?.label ?? type;
  }

  getQuestionCount(exercise: Exercise): number {
    return exercise.content?.questions?.length ?? 0;
  }

  private clampExercisePage(): void {
    const maxPage = Math.max(Math.ceil(this.filteredExercises.length / this.exercisePageSize) - 1, 0);
    this.exercisePage = Math.max(0, Math.min(this.exercisePage, maxPage));
  }
}
